import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { AuthApiError } from '../services/authService';

interface PasswordStrengthIndicatorProps {
  password: string;
  isVisible: boolean;
}

function PasswordStrengthIndicator({ password, isVisible }: PasswordStrengthIndicatorProps) {
  if (!isVisible) return null;

  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[@$!%*?&]/.test(password);
  const hasMinLength = password.length >= 8;

  const requirements = [
    { text: 'At least 8 characters', met: hasMinLength },
    { text: 'One uppercase letter', met: hasUppercase },
    { text: 'One lowercase letter', met: hasLowercase },
    { text: 'One number', met: hasNumber },
    { text: 'One special character (@$!%*?&)', met: hasSpecial },
  ];

  return (
    <div className="mt-2 p-3 bg-muted/50 rounded-md text-sm">
      <p className="font-medium text-foreground mb-2">Password requirements:</p>
      <ul className="space-y-1">
        {requirements.map((req, index) => (
          <li key={index} className={`flex items-center ${req.met ? 'text-green-600' : 'text-muted-foreground'}`}>
            <svg className={`w-4 h-4 mr-2 ${req.met ? 'text-green-600' : 'text-muted-foreground'}`} fill="currentColor" viewBox="0 0 20 20">
              {req.met ? (
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              ) : (
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2a1 1 0 102 0V7zm-1 4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
              )}
            </svg>
            {req.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const passwordsMatch = password === confirmPassword;

  const isPasswordStrong = (pwd: string): boolean => {
    return pwd.length >= 8 &&
           /[A-Z]/.test(pwd) &&
           /[a-z]/.test(pwd) &&
           /\d/.test(pwd) &&
           /[@$!%*?&]/.test(pwd);
  };

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }
    setToken(tokenParam);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    if (!isPasswordStrong(password)) {
      setError('Password does not meet security requirements');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await resetPassword(token, password);
      // User is automatically logged in after successful reset
      navigate('/dashboard');
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contrarian OS | Reset Password</title>
      </Helmet>
      <div className="min-h-screen flex">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-primary items-center justify-center p-12">
          <div className="text-center text-primary-foreground">
            <div className="flex items-center justify-center space-x-3 mb-8">
              <img 
                src="/logo.svg" 
                alt="Contrarian OS" 
                className="h-12 w-auto filter brightness-0 invert"
              />
              <h1 className="text-3xl font-bold">
                Contrarian OS
              </h1>
            </div>
            <h2 className="text-xl font-semibold mb-4">
              Secure Password Reset
            </h2>
            <p className="text-primary-foreground/80 max-w-md">
              Choose a strong password to keep your account secure.
            </p>
          </div>
        </div>

        {/* Right side - Reset Password Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="text-center mb-8 lg:hidden">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <img 
                  src="/logo.svg" 
                  alt="Contrarian OS" 
                  className="h-8 w-auto"
                />
                <h1 className="text-2xl font-bold text-foreground">
                  Contrarian OS
                </h1>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Reset Your Password
              </h2>
              <p className="text-muted-foreground">
                Enter your new password below
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {!token ? (
              <div className="text-center">
                <div className="mb-4">
                  <svg className="w-16 h-16 mx-auto text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Invalid Reset Link
                </h3>
                <p className="text-muted-foreground mb-6">
                  This password reset link is invalid or has expired. Please request a new password reset.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="bg-primary text-primary-foreground px-6 py-2 rounded-md hover:bg-primary/90 transition-colors"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                    New Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                    placeholder="Enter your new password"
                    disabled={isLoading}
                  />
                  <PasswordStrengthIndicator 
                    password={password} 
                    isVisible={password.length > 0}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground ${
                      !passwordsMatch && confirmPassword.length > 0 ? 'border-destructive' : 'border-border'
                    }`}
                    placeholder="Confirm your new password"
                    disabled={isLoading}
                  />
                  {!passwordsMatch && confirmPassword.length > 0 && (
                    <p className="text-sm text-destructive mt-1">Passwords do not match</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !passwordsMatch || !isPasswordStrong(password)}
                  className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
} 